/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { Button, Theme, Typography } from "@material-ui/core";
import BackIcon from "@material-ui/icons/ArrowBackIos";
import CloudOff from "@material-ui/icons/CloudOff";
import CloudIcon from "@material-ui/icons/CloudUploadOutlined";
import SyncIcon from "@material-ui/icons/Sync";
import { createStyles, makeStyles } from "@material-ui/styles";
import { DataNode, DFSValueType, NIFormData, parseFieldIdentifier, walkData } from './form/services';
import { isNullOrUndefined, rescopeDataPath } from './form/utils';
import { ErrorObject } from "ajv";
import { has, set } from "json-pointer";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { createSelector } from "reselect";
import { Observable } from "rxjs";
import { SyncStatus } from "../../evaluation";
import { AppState, beginPublish, fetchEvalPatchesActions, followEval, getFormValue, loadEvaluation, publishPromptActions, redirect, removeFormValue, scanImagesToUpload, scanUncreatedEvals, setActiveEval, setFormValue } from "../../redux";
import { ImageServiceImpl } from "../services/ImageService";
import { FormWrapper } from "./form/components/Form";
import SubmitOverlay from "./SubmitOverlay";
import { selectCertifications } from './EvalListItem';
import { selectRoles } from './form/components/DataImport';
import config from '../../config';
import moment from 'moment';
import { getLocalDefinition, hasLocalDefinition } from '../../definitions/index';

const NULL_VALUE = '__NULL__';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      overflow: "hidden",
      [theme.breakpoints.down("xs")]: {
        marginTop: "56px",
      },
    },
    navLinks: {
      backgroundColor: theme.palette.primary.main,
      alignItems: "stretch",
    },
    link: {
      display: "flex",
      height: "40px",
      marginLeft: theme.spacing(2),
    },
    editorOverlayPaper: {
      height: "100%",
    },
    editorOverlay: {
      height: "calc(100%)",
    },
    tocFooter: {
      display: "flex",
      flexDirection: "column",
    },
    tocFooterButton: {
      margin: theme.spacing(1),
      display: "flex",
      justifyContent: "flex-end",
      height: "30px",
      alignItems: "center",
      textTransform: "none",
    },
    submitButton: {
      verticalAlign: "middle",
      height: "48px",
      borderRadius: 0,
    },
    syncStatusText: {
      // textDecoration: "underline",
      marginRight: theme.spacing(1),
    },
    backButton: {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      textDecoration: "none",
    },
    formWrapper: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1
    }
  })
);

const selectFormType = (state: AppState, props: EvalEditorProps) => {
  if (!state.evals.items.has(props.match.params.evalId)) {
    return null;
  }

  const evalMeta = state.evals.items.get(props.match.params.evalId)!;
  return evalMeta.formType;
};

const selectCertificationStatus = createSelector(
  selectCertifications,
  selectFormType,
  (certifications, formType) => {
    if (!formType) {
      return false;
    } else {
      return certifications[formType];
    }
})

const selectCanPublish = createSelector(
  selectCertificationStatus,
  status => {
    if (!status) {
      return false;
    }
    if (Array.isArray(status)) {
      if (status.findIndex(status => status !== 'expired') >= 0) {
        return true;
      }
    } else if ((status as any).allow_publish) {
      return true;
    }
    return false;
})

const selectUuid = createSelector(
  (state: AppState, props: EvalEditorProps) => state.evals.items.getIn([props.match.params.evalId, "uuid"]),
  rv => rv
);
const selectForm = createSelector(
  function formTypeSelector(state: AppState, props: EvalEditorProps) {
    const evl = state.evals.items.get(props.match.params.evalId);
    return evl?.formType ?? null;
  },
  function formVersionSelector(state: AppState, props: EvalEditorProps) {
    const evl = state.evals.items.get(props.match.params.evalId);
    return evl?.formVersion ?? null;
  },
  function formMapSelector(state: AppState) {
    return state.forms.items;
  },
  function loadForm(formType, formVersion, forms) {
    if (formType === null || formVersion === null || forms === null) return null;

    const versions = forms[formType];
    if (versions === null) return null;

    if (hasLocalDefinition(formType)) {
      return getLocalDefinition(formType);
    }

    return versions[formVersion] ?? null;
  }
);
const selectPublished = createSelector(
  (state: AppState, props: EvalEditorProps) => state.evals.items.getIn([props.match.params.evalId, "published"]),
  published => published
)
const selectFirstPublished = createSelector(
  (state: AppState, props: EvalEditorProps) => state.evals.items.getIn([props.match.params.evalId, "firstPublished"]),
  firstPublished => firstPublished
)
const selectSyncState = createSelector(
  (state: AppState, props: EvalEditorProps) => state.evals.items.getIn([props.match.params.evalId, "syncStatus"]),
  (rv?: SyncStatus) => rv ?? null
);
const selectScenarioName = createSelector(
  (state: AppState, props: EvalEditorProps) => state.evals.items.getIn([props.match.params.evalId, "scenarioName"]),
  (scenarioName?: string) => scenarioName ?? null
);
const selectEvaluatorId = createSelector(
  (state: AppState, props: EvalEditorProps) => {
    const idField = [props.match.params.evalId, "data", "evaluator", "id"];
    if (state.evals.items.hasIn(idField)) {
      return state.evals.items.getIn(idField);
    }

    return null;
  },
  rv => rv
);
const selectSubmitState = createSelector(
  (state: AppState) => state.interface.submitState.submitting,
  (state: AppState) => state.interface.submitState.error,
  (state: AppState) => state.interface.submitState.step,
  (submitting, error, step) => ({ submitting, error, step })
);
const selectUserId = createSelector(
  (state: AppState) => state.session.dfdmToken!.sub,
  rv => rv
);

const selectCanEdit = createSelector(
  selectFormType,
  selectFirstPublished,
  (formType, firstPublished) => {
    if (!firstPublished) {
      return true;
    }
    if (!formType) {
      return false;
    }
    const postPublishLockInterval = config.versionMapping[formType].postPublishLockInterval;
    if (!postPublishLockInterval || postPublishLockInterval === -1) {
      return true;
    }
    if (firstPublished) {
      const deadline = moment(firstPublished).add(postPublishLockInterval, 'd');
      return moment().isBefore(deadline);
    }

    return true;
})

interface EvalEditorProps extends RouteComponentProps<{ evalId: string }> { }

const EvalEditor: React.FC<EvalEditorProps> = props => {
  const activeEvalId = decodeURIComponent(props.match.params.evalId);

  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [data$, setData$] = useState<Observable<NIFormData>>();
  const [dirty, setDirty] = useState(false);

  const store = useStore();
  const dispatch = useDispatch();
  const published = useSelector<AppState, any>(state => selectPublished(state, props));
  const syncStatus = useSelector<AppState, any>(state => selectSyncState(state, props));
  const scenarioName = useSelector<AppState, any>(state => selectScenarioName(state, props));
  const form = useSelector<AppState, any>(state => selectForm(state, props));
  const canPublish = useSelector<AppState, any>(state => selectCanPublish(state, props));
  const canEdit = useSelector<AppState, any>(state => selectCanEdit(state, props));
  const roles = useSelector(selectRoles);
  const latestData = useRef<NIFormData>();

  useEffect(function loadEval() {
    dispatch(setActiveEval(activeEvalId, props.history));
    dispatch(loadEvaluation(activeEvalId));
    setData$(followEval(store, activeEvalId));
  }, [activeEvalId, props.history, store, dispatch]);

  useEffect(
    function waitForLoaded() {
      // form is implicitly only available when eval is loaded since it needs properties of the eval to load
      setIsLoaded(form !== null && form !== undefined);
    },
    [form]
  );

  useLayoutEffect(function trackData() {

    if (!data$) return;

    const subscription = data$.subscribe(newData => (latestData.current = newData));

    return () => subscription.unsubscribe();
  }, [data$]);

  // FUI-1455 - In some unexplained cases, the node for some questions with multiple sub-questions (ie q11) is lost
  // We traverse the form def to find such objects and re-set the empty object if no data is found at this location
  // This is for validation purposes only, so that the UI can show missing required fields. No patches are generated for these empty objects 
  useEffect(
    () => {
      if (isLoaded && latestData && latestData.current) {
        const ev = latestData.current;
        const i = /\/properties\//g;
        walkData(form.definition.data, function visitor(node: DataNode, nodeName: string) {
            if (nodeName === '') {
              return;
            }
            const path = nodeName.replace(i, '/');
            if (node.properties && !has(ev, path)) {
              set(ev, path, {});
            }
            if (node.allOf) {
                for (const childNode of node.allOf) {
                  walkData(childNode, visitor, nodeName)
                }
            }
            if (node.anyOf) {
              for (const childNode of node.anyOf) {
                walkData(childNode, visitor, nodeName)
              }
            }
            if (node.then) {
              walkData(node.then, visitor, nodeName)
            }
        });
        latestData.current = ev;
      }      
    },
    [isLoaded, form, latestData]
  );

  const classes = useStyles({});

  const uuid = useSelector<AppState, any>(state => selectUuid(state, props));
  const handleSync = useCallback(() => {
    if (!form) return;

    if (uuid) {
      dispatch(fetchEvalPatchesActions.request(uuid));
      dispatch(scanImagesToUpload());
    } else {
      dispatch(scanUncreatedEvals());
    }
  }, [form, uuid, dispatch]);

  const [errors, setErrors] = useState<number>(0);

  const evaluatorId = useSelector<AppState, any>(state => selectEvaluatorId(state, props));
  const userId = useSelector(selectUserId);
  // const showPatchFailureDialog = useSelector<AppState, boolean>(state => selectShowPatchFailure(state, props));
  const canSubmit = useMemo(() => {
    const roleNames = roles ? Object.values(roles) : [];
    return published === false &&
      ((evaluatorId === userId) || roleNames?.includes('ROLE_ADMIN') || roleNames?.includes('ROLE_EVALUATOR_LIAISON')) &&
      errors === 0 &&
      syncStatus === "COMPLETE" &&
      canPublish &&
      canEdit;
  }, [published, evaluatorId, userId, errors, syncStatus, roles]);

  const doSubmit = useCallback(() => {
    if (!activeEvalId) {
      // Is this check even necessary? To submit we had to get the active eval ID...
      alert("Error. Please refresh and try again after a few moments. If problem persists please contact support");
      return;
    }

    dispatch(beginPublish(activeEvalId));
  }, [dispatch, activeEvalId]);
  
  const imageService = useMemo(() => new ImageServiceImpl(dispatch), [dispatch]);

  const footer = useMemo(() => {
    let syncText: string;
    let SyncStatusIcon;

    if (syncStatus === 'FAILED') {
      syncText = "Failed to Sync";
      SyncStatusIcon = CloudOff;
    } else if (syncStatus === "PENDING") {
      syncText = "Pending Upload...";
      SyncStatusIcon = CloudIcon;
    } else {
      syncText = `All Data Uploaded`;
      SyncStatusIcon = SyncIcon;
    }

    return (
      <div className={classes.tocFooter}>
        <Button color="primary" variant="text" className={classes.tocFooterButton} onClick={handleSync}>
          <Typography variant="caption" className={classes.syncStatusText}>
            {syncText}
          </Typography>
          <SyncStatusIcon />
        </Button>

        <Button
          disabled={!canSubmit}
          fullWidth
          color="secondary"
          className={classes.submitButton}
          variant="contained"
          onClick={doSubmit}
        >
          {published ? 'Published' : 'Submit'}
        </Button>
      </div>
    );
  }, [syncStatus, handleSync, classes, canSubmit, doSubmit, published]);

  const handleSetData = useCallback((path: string, scope: string, newValue: DFSValueType) => {
    const resolvedPath = rescopeDataPath(path, scope);

    // don't set duplicates here, it just creates unecessary patches.
    const currentValue = getFormValue(path, latestData.current!, "");
    if (currentValue === newValue || (isNullOrUndefined(currentValue) && isNullOrUndefined(newValue))) {
      return;
    }

    if (newValue === NULL_VALUE) {
      setDirty(true);
      dispatch(setFormValue(resolvedPath, null, latestData.current!, activeEvalId));
    } else if (newValue !== null) {
      setDirty(true);
      dispatch(setFormValue(resolvedPath, newValue, latestData.current!, activeEvalId));
    }
    else if (has(latestData.current!, parseFieldIdentifier(resolvedPath))) {
      setDirty(true);
      dispatch(removeFormValue(resolvedPath, activeEvalId));
    }
  }, [activeEvalId, dispatch]);

  const handleRemoveData = useCallback((path: string, scope: string) => {
      const resolvedPath = rescopeDataPath(path, scope);

      if (has(latestData.current!, parseFieldIdentifier(resolvedPath))) {
        setDirty(true);
        dispatch(removeFormValue(resolvedPath, activeEvalId));
      }
    }, [activeEvalId, dispatch]);

  const submitState = useSelector(selectSubmitState);

  // problem: every time sync status changes, footer rerenders which rerenders entire form control :)
  const formControl = useMemo(
    () => {
      return (
        <FormWrapper
          form={form!}
          data$={data$!}
          evalID={activeEvalId}
          scenarioName={scenarioName}
          canEdit={canEdit}
          compressedToCHeader={
            <div>
              <Link className={classes.backButton} to={"/evals"}>
                <Button className={classes.backButton}>
                  <BackIcon />
                  Back
              </Button>
              </Link>
              {/* <div className={classes.navLinks}>
          <NavLinks orientation="drawer" />
        </div> */}
            </div>
          }
          onErrors={(errors: ErrorObject[]) => setErrors(errors.length)}
          onSetData={handleSetData}
          onRemoveData={handleRemoveData}
          imageService={imageService}
          // TODO: Implement expression result tracking and validity
          tocFooter={footer}
        />
      )
    },
    [form, data$, activeEvalId, handleSetData, handleRemoveData, imageService, footer, classes.backButton, canEdit]
  );

  if (!isLoaded) {
    return <div />;
  }

  return (
    <div className={classes.root}>
      {formControl}
      {submitState.submitting && (
        <SubmitOverlay
          {...submitState}
          history={props.history}
          onFinishedPrompts={() => {
            dispatch(publishPromptActions.success(uuid));
          }}
          onSubmitted={() => {
            dispatch(redirect("/evals", props.history));
          }}
        />
      )}
    </div>
  );
};

export default EvalEditor;